/**
 * Dashboard Incident Button
 *
 * A tiny presentational component that renders the "Incidents" action
 * button on the Dashboard's per-event action row. Extracted from
 * `Dashboard.tsx` so its visibility predicate, ARIA label, and
 * navigation behaviour can be property-tested in isolation, without
 * needing to spin up the entire Dashboard data stack.
 *
 * Feature: incident-reporting
 * Requirements: 12.5, 17.1–17.18
 *
 * Visibility predicate (render in DOM iff ALL hold) — Req 17.1–17.6, 17.13,
 * 17.14, 17.17:
 *
 *     (showCoachFeatures || showAdminFeatures)
 *       && !isCanceled
 *       && (now ?? new Date()) > new Date(event.event_starttime)
 *
 * When any clause is false the component returns `null` so the DOM row
 * does not reserve layout space for a hidden control (Req 17.17).
 *
 * Styling (Req 17.7, 17.8, 17.9): matches the existing orange CTA style
 * used elsewhere on the Dashboard — rendered via the shared `Button`
 * component's `orange` variant so the orange background wins the
 * Tailwind cascade deterministically (the previous approach of passing
 * orange utilities through `className` lost to `bg-primary-*` from the
 * default `primary` variant because `bg-primary-*` appears later in the
 * compiled stylesheet). The tap target is held at `min-h-[48px]` so it
 * exceeds the 44px WCAG 2.2 minimum even on the smallest supported
 * viewport (320×568).
 *
 * Accessibility (Req 17.16): the button's `aria-label` is the literal
 * template `Report incident for ${event.event_name}` so screen readers
 * announce the event context rather than the ambiguous "Incidents" label.
 *
 * onClick (Req 17.10): `e.stopPropagation()` to keep the outer list row's
 * click handler (which toggles event details) from firing, then
 * `navigate(`/events/${event.event_id}/incidents`, { state: { ... } })`.
 * The capture dialog itself is NEVER opened from the Dashboard — the
 * dashboard is a pure navigation surface for incident capture.
 *
 * See: design.md §Components and Interfaces → Component hierarchy
 *      (detail) → Dashboard.tsx; §Frontend Design → Mobile-first layout
 *      notes.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../common/Button';
import type { Event as ApiEvent } from '../../../api/rest-client';

export interface DashboardIncidentButtonProps {
  /** The event the button reports against — supplies `event_id`, `event_name`, and `event_starttime`. */
  event: Pick<ApiEvent, 'event_id' | 'event_name' | 'event_starttime'>;
  /** True when the event carries `event_status === 'canceled'` — hides the button (Req 17.13). */
  isCanceled: boolean;
  /** True when the Dashboard API response indicates the viewer has coach features. */
  showCoachFeatures: boolean;
  /** True when the Dashboard API response indicates the viewer has admin features. */
  showAdminFeatures: boolean;
  /**
   * Optional override for the "now" boundary. Falls back to `new Date()`
   * when omitted. Exposed so property tests can drive the time boundary
   * deterministically without stubbing global `Date`.
   */
  now?: Date;
}

/**
 * Shared visibility predicate. Exported for use by the early-return guard
 * inside `Dashboard.tsx` so the same expression controls both whether the
 * whole action row renders and whether this individual button renders.
 * Mirrors the expression documented in the component header.
 */
export function shouldShowIncidentButton(params: {
  isCanceled: boolean;
  showCoachFeatures: boolean;
  showAdminFeatures: boolean;
  eventStartTime: string;
  now?: Date;
}): boolean {
  const { isCanceled, showCoachFeatures, showAdminFeatures, eventStartTime, now } = params;
  const nowDate = now ?? new Date();
  const startDate = new Date(eventStartTime);
  // Guard against malformed event_starttime: an invalid Date compared to
  // any other Date with `>` returns false, so the button stays hidden,
  // which is the correct safe default.
  return (
    (showCoachFeatures || showAdminFeatures) &&
    !isCanceled &&
    nowDate > startDate
  );
}

export function DashboardIncidentButton({
  event,
  isCanceled,
  showCoachFeatures,
  showAdminFeatures,
  now,
}: DashboardIncidentButtonProps): React.ReactElement | null {
  const navigate = useNavigate();

  const visible = shouldShowIncidentButton({
    isCanceled,
    showCoachFeatures,
    showAdminFeatures,
    eventStartTime: event.event_starttime,
    now,
  });

  if (!visible) {
    return null;
  }

  return (
    <Button
      variant="orange"
      onClick={(e) => {
        e.stopPropagation();
        navigate(`/events/${event.event_id}/incidents`, {
          state: {
            event_name: event.event_name,
            event_starttime: event.event_starttime,
          },
        });
      }}
      className="text-base px-6 py-3 min-h-[48px]"
      aria-label={`Report incident for ${event.event_name}`}
    >
      Incidents
    </Button>
  );
}

export default DashboardIncidentButton;
